/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useState } from "react";
import InquiryToCustomer, { InquiryToCustomerStatus } from "../../../types/Entities/InquiryToCustomer";
import { Box } from "@mui/material";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { FormProvider, useForm } from "../../../provider/Form/FormProvider";
import ButtonLoading from "../../Button/ButtonLoading";
import Group from "../../Wizard/Group";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import InquiryToCustomerMediaObjects from "./InquiryToCustomerMediaObjects";
import { useUpdateInquiryToCustomer } from "@/src/hooks/queries/inquiryToCustomer";
import * as messagesActions from "../../../store/messages/actions";
import { useNavigate } from "react-router";
import { getCaseLink } from "../../../services/productService";
import ProductBox from "@/src/components/ProductBox/ProductBox";
import { apiPatch } from "../../../services/apiClient";

type InquiryToCustomerDecisionBoxProps = {
  inquiryToCustomer: InquiryToCustomer;
  product: AbstractCase;
};

export default function InquiryToCustomerDecisionBox({ inquiryToCustomer, product }: InquiryToCustomerDecisionBoxProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const updateInquiryToCustomer = useUpdateInquiryToCustomer();
  const navigate = useNavigate();

  let initialValues = {
    inquiryDecisionAnswer: null,
  };

  const handleSubmit = async ({ values }: { values: Partial<InquiryToCustomer> }) => {
    setIsLoading(true);
    let payload: Partial<InquiryToCustomer> = {
      status: InquiryToCustomerStatus.STATUS_DONE,
      doneBy: "customer",
      inquiryDecisionAnswer: values.inquiryDecisionAnswer,
    };

    await updateInquiryToCustomer.mutateAsync({ id: inquiryToCustomer.id, data: payload });
    setIsLoading(false);

    messagesActions.addMessage({
      type: "success",
      text: "Rückmeldung erfolgreich abgegeben",
    });
    navigate(getCaseLink(product) + "/anfrage/beantwortet");
  };

  return (
    <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
      <InquiryToCustomerDecisionForm inquiryToCustomer={inquiryToCustomer} isLoading={isLoading} />
    </FormProvider>
  );
}

type InquiryToCustomerDecisionFormProps = {
  inquiryToCustomer: InquiryToCustomer;
  isLoading: boolean;
};

const InquiryToCustomerDecisionForm = ({ inquiryToCustomer, isLoading }: InquiryToCustomerDecisionFormProps) => {
  const { handleSubmit, values } = useForm();

  return (
    <ProductBox headline={"Benötigte Informationen"}>
      <Box component={"form"} onSubmit={handleSubmit}>
        <InquiryToCustomerMediaObjects inquiryToCustomer={inquiryToCustomer} />
        <Box dangerouslySetInnerHTML={{ __html: inquiryToCustomer.inquiryDecisionQuestion ?? "" }} />
        <Group
          direction={"row"}
          type="radio"
          name="inquiryDecisionAnswer"
          question={""}
          options={[
            {
              labelText: "Ja",
              labelIcon: <CheckIcon />,
              labelIconActive: <CheckIcon active />,
              value: "yes",
            },
            {
              labelText: "Nein",
              labelIcon: <CrossIcon />,
              labelIconActive: <CrossIcon active />,
              value: "no",
            },
          ]}
        />
        <Box sx={{ marginTop: "1.5rem" }}>
          <ButtonLoading
            fullWidth
            variant={"contained"}
            color={"primary"}
            type={"submit"}
            isLoading={isLoading}
            disabled={values.inquiryDecisionAnswer === null}
          >
            Rückmeldung abschicken
          </ButtonLoading>
        </Box>
      </Box>
    </ProductBox>
  );
};
